import React, { useMemo } from "react";
import { format, isValid, parseISO } from "date-fns";
import { GlobalTableActionButton } from "../../../../../global/GlobalTableActionButton";
import GlobalDirectoryFiltersSection from "../../../../../global/directory/GlobalDirectoryFiltersSection";
import GlobalDirectoryGridSection from "../../../../../global/directory/GlobalDirectoryGridSection";
import {
  GlobalDirectoryShell,
  GlobalDirectoryColumnPicker,
  GlobalDirectoryExportMenu,
  GlobalDirectoryPaginationSection,
} from "../../../../../global/directory";

export const TOURNAMENT_LIST_COLUMN_VISIBILITY_KEY = "girinestham.tournamentList.columnVisibility";

export const TOURNAMENT_DIRECTORY_COLUMN_DEFS = [
  { id: "tournament_code", label: "Code", defaultVisible: true },
  { id: "tournament_name", label: "Tournament", defaultVisible: true },
  { id: "sport_name", label: "Sport", defaultVisible: true },
  { id: "level_name", label: "Level", defaultVisible: true },
  { id: "tournament_date", label: "Date", defaultVisible: true },
  { id: "tournament_time", label: "Time", defaultVisible: true },
  { id: "tournament_location", label: "Location", defaultVisible: true },
  { id: "registration_last_date", label: "Reg. closes", defaultVisible: true },
  { id: "organizer_name", label: "Organizer", defaultVisible: true },
  { id: "contact_number", label: "Organizer contact", defaultVisible: true },
  { id: "participation_fee", label: "Fee", defaultVisible: true },
  { id: "max_students_allowed", label: "Max students", defaultVisible: true },
  { id: "update_name", label: "Tournament update", defaultVisible: true },
  { id: "total_registered", label: "Registered", defaultVisible: true },
  { id: "total_collected_fee", label: "Collected", defaultVisible: true },
  { id: "description", label: "Description", defaultVisible: true },
  { id: "created_at", label: "Created", defaultVisible: true },
  { id: "updated_at", label: "Updated", defaultVisible: true },
];

function loadTournamentColumnVisibility() {
  try {
    const raw = localStorage.getItem(TOURNAMENT_LIST_COLUMN_VISIBILITY_KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    /* ignore */
  }
  return null;
}

export function createInitialTournamentColumnVisibility() {
  const saved = loadTournamentColumnVisibility();
  const initial = {};
  for (const c of TOURNAMENT_DIRECTORY_COLUMN_DEFS) {
    initial[c.id] = saved?.[c.id] ?? c.defaultVisible;
  }
  return initial;
}

function formatMoney(value) {
  if (value == null || value === "") return "—";
  const n = Number(value);
  return Number.isFinite(n) ? `₹${n.toFixed(2)}` : "—";
}

function formatDateCell(value) {
  if (value == null || value === "") return "—";
  try {
    const raw = String(value).trim();
    let d = parseISO(raw);
    if (!isValid(d) && /^\d{4}-\d{2}-\d{2}/.test(raw)) {
      d = new Date(raw.replace(" ", "T"));
    }
    if (!isValid(d)) d = new Date(raw);
    return isValid(d) ? format(d, "PP") : "—";
  } catch {
    return "—";
  }
}

function formatTimeCell(value) {
  if (value == null || value === "") return "—";
  const s = String(value);
  if (s.length >= 5 && s.includes(":")) return s.slice(0, 5);
  return s;
}

export function tournamentDirectoryCellValue(columnId, t) {
  switch (columnId) {
    case "participation_fee":
    case "total_collected_fee":
      return formatMoney(t[columnId]);
    case "tournament_date":
    case "registration_last_date":
    case "created_at":
    case "updated_at":
      return formatDateCell(t[columnId]);
    case "tournament_time":
      return formatTimeCell(t.tournament_time);
    case "description": {
      const text = t.description;
      if (text == null || String(text).trim() === "") return "—";
      const str = String(text);
      return str.length > 120 ? `${str.slice(0, 117)}…` : str;
    }
    default:
      return t[columnId] ?? "—";
  }
}

export function tournamentDefaultDirectoryFilters() {
  return {
    search: "",
    column: "",
    value: "",
  };
}

function TournamentFilters({
  draftFilters,
  onDraftChange,
  onClear,
  tournaments,
  secondaryStart,
  secondaryEnd,
}) {
  const valueFilterOptions = useMemo(() => {
    if (!draftFilters.column) return [];
    const seen = new Set();
    const options = [];
    for (const row of tournaments) {
      const raw = row[draftFilters.column];
      if (raw == null || raw === "") continue;
      const label = String(raw).trim();
      if (!label) continue;
      if (!seen.has(label)) {
        seen.add(label);
        options.push({ value: label, label });
      }
    }
    return options.sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: "base" }),
    );
  }, [draftFilters.column, tournaments]);

  return (
    <GlobalDirectoryFiltersSection
      className="ub-filters ub-directory-filters"
      searchLabel="Search by name or code"
      searchPlaceholder="Type to filter…"
      searchValue={draftFilters.search}
      onSearchChange={(v) => onDraftChange({ ...draftFilters, search: v })}
      columnLabel="Column"
      columnValue={draftFilters.column}
      onColumnChange={(v) => onDraftChange({ ...draftFilters, column: v, value: "" })}
      gridColumnDefs={TOURNAMENT_DIRECTORY_COLUMN_DEFS}
      columnPlaceholderOption="Select"
      valueLabel="Value"
      valueValue={draftFilters.value}
      onValueChange={(v) => onDraftChange({ ...draftFilters, value: v })}
      valueOptions={valueFilterOptions}
      valuePlaceholderOption="All"
      onClear={onClear}
      clearFilterLabel="Clear filter"
      secondaryStart={secondaryStart}
      secondaryEnd={secondaryEnd}
    />
  );
}

function TournamentDirectoryGrid({
  tournaments,
  columnVisibility,
  loading,
  onDelete,
  onRequestEdit,
}) {
  const visibleDataColumns = useMemo(
    () => TOURNAMENT_DIRECTORY_COLUMN_DEFS.filter((c) => columnVisibility[c.id]),
    [columnVisibility],
  );

  const tableHeaders = useMemo(
    () => [
      ...visibleDataColumns.map((c) => ({ key: c.id, label: c.label })),
      {
        key: "actions",
        label: "Actions",
        className: "ub-table__col-actions",
      },
    ],
    [visibleDataColumns],
  );

  const showRows = !loading && tournaments.length > 0;

  return (
    <GlobalDirectoryGridSection
      loading={loading}
      loadingMessage="Loading tournaments…"
      headers={tableHeaders}
      isEmpty={!loading && tournaments.length === 0}
      emptyMessage="No tournaments found."
      tableAriaLabel="Tournaments directory"
      wrapClassName="ub-table-wrap"
      tableClassName="ub-table"
    >
      {showRows
        ? tournaments.map((t) => (
            <tr key={t.id}>
              {visibleDataColumns.map((col) => {
                const v = tournamentDirectoryCellValue(col.id, t);
                return (
                  <td key={col.id}>
                    {col.id === "tournament_name" ? <span style={{ fontWeight: 500 }}>{v}</span> : v}
                  </td>
                );
              })}
              <td className="ub-table__col-actions">
                <div className="ub-row-actions">
                  <GlobalTableActionButton action="edit" onClick={() => onRequestEdit(t)} />
                  <GlobalTableActionButton action="delete" onClick={() => onDelete(t.id)} />
                </div>
              </td>
            </tr>
          ))
        : null}
    </GlobalDirectoryGridSection>
  );
}

export default function TournamentDirectorySection({
  tournaments,
  draftDirectoryFilters,
  onDraftDirectoryFiltersChange,
  onClearDirectoryFilters,
  columnVisibility,
  onColumnVisibilityChange,
  getTournamentExportData,
  exportFilenameBase = "admin_tournaments_directory",
  loadingTournaments,
  directoryRows,
  directoryPage,
  directoryTotalPages,
  directoryTotal,
  directoryPageSize,
  onDirectoryPageChange,
  onDelete,
  onRequestEdit,
}) {
  return (
    <div id="td-list" className="user-builder-panel" style={{ scrollMarginTop: "2rem" }}>
      <div className="user-builder-panel__head">
        <h3 className="user-builder-panel__title">Directory</h3>
        <p className="user-builder-panel__hint">Active tournaments only. Delete marks inactive.</p>
      </div>
      <GlobalDirectoryShell
        filters={
          <TournamentFilters
            draftFilters={draftDirectoryFilters}
            onDraftChange={onDraftDirectoryFiltersChange}
            onClear={onClearDirectoryFilters}
            tournaments={tournaments}
            secondaryStart={
              <GlobalDirectoryColumnPicker
                columnDefs={TOURNAMENT_DIRECTORY_COLUMN_DEFS}
                visibility={columnVisibility}
                onVisibilityChange={onColumnVisibilityChange}
                storageKey={TOURNAMENT_LIST_COLUMN_VISIBILITY_KEY}
              />
            }
            secondaryEnd={
              <GlobalDirectoryExportMenu getExportData={getTournamentExportData} filenameBase={exportFilenameBase} />
            }
          />
        }
        approvals={null}
        grid={
          <TournamentDirectoryGrid
            tournaments={directoryRows}
            columnVisibility={columnVisibility}
            loading={loadingTournaments}
            onDelete={onDelete}
            onRequestEdit={onRequestEdit}
          />
        }
        pagination={
          <GlobalDirectoryPaginationSection
            page={directoryPage}
            totalPages={directoryTotalPages}
            total={directoryTotal}
            pageSize={directoryPageSize}
            onPageChange={onDirectoryPageChange}
            label="Tournament directory pagination"
          />
        }
      />
    </div>
  );
}
